import { useState } from "react";
import { useQuery, getPlaybooks, deletePlaybook } from "wasp/client/operations";
import { Plus } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { Button } from "../client/components/ui/button";
import { Skeleton } from "../client/components/ui/skeleton";
import { useToast } from "../client/hooks/use-toast";
import PlaybookForm from "./PlaybookForm";
import PlaybookCard from "./PlaybookCard";
import PlaybookEmptyState from "./PlaybookEmptyState";
import EdgyHeaderButton from "../edgy/EdgyHeaderButton";

/**
 * Liste des playbooks. Un trader en tient plusieurs pour comparer des stratégies
 * et suivre leur évolution, chacun ciblant ses propres comptes.
 */
export default function PlaybookPage() {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: playbooks, isLoading, refetch } = useQuery(getPlaybooks);

  // null = liste ; "new" = création ; sinon le playbook en cours d'édition.
  const [editing, setEditing] = useState<"new" | any | null>(null);

  function handleSaved() {
    setEditing(null);
    refetch();
  }

  async function handleDelete(playbook: any) {
    if (!window.confirm(t.playbook.deleteConfirm)) return;
    try {
      await deletePlaybook({ id: playbook.id });
      toast({ description: t.playbook.deletedToast });
      refetch();
    } catch (err: any) {
      toast({ variant: "destructive", description: err?.message || t.playbook.deleteError });
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 flex flex-col gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (editing) {
    return (
      <div className="px-4 py-8">
        <PlaybookForm
          playbook={editing === "new" ? undefined : editing}
          onSaved={handleSaved}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  const list = playbooks ?? [];

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display">{t.playbook.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t.playbook.subtitle}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <EdgyHeaderButton />
          <Button variant="outline" size="sm" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4 mr-1" />
            {t.playbook.newPlaybook}
          </Button>
        </div>
      </div>

      {list.length === 0 ? (
        <PlaybookEmptyState onCreate={() => setEditing("new")} />
      ) : (
        <div className="flex flex-col gap-4">
          {list.map((p: any) => (
            <PlaybookCard
              key={p.id}
              playbook={p}
              onEdit={() => setEditing(p)}
              onDelete={() => handleDelete(p)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
